import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  BookOpen,
  Calendar,
  Camera,
  CheckCircle2,
  Crown,
  Image as ImageIcon,
  Pencil,
  Plus,
  ScrollText,
  Sprout,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Stagger, StaggerItem } from "@/components/FadeIn";
import { M3Card, M3CardHeader } from "@/components/M3Shared";
import { M3BottomSheet } from "@/components/M3BottomSheet";
import { MediaLightbox, type LightboxMedia } from "@/components/chat/MediaLightbox";
import { MemberAvatar } from "@/components";
import { registerFab } from "@/components/FabRegistry";
import { useCurrentUser } from "@/lib/useCurrentUser";
import { useCurrentFamily } from "@/lib/useCurrentFamily";
import { usePublishModuleSlot } from "@/lib/ModuleContext";
import { useRealtimeTable, useSupabaseQuery } from "@/lib/dbHooks";
import { getCachedPhoto, putCachedPhoto, deleteCachedPhoto, sha256Hex } from "@/lib/photoCache";
import { useCrestUpload, type CrestUploadState } from "@/lib/useCrestUpload";
import { cn } from "@/lib/utils";
import { supabase, isAuthConfigured } from "@/lib/supabase";
import type { Database } from "@/types/db";

// 1-hour signed-URL TTL for family photo thumbnails. Photos rarely
// change in a single session; a long TTL avoids re-issuing URLs on
// every realtime refresh. FamilyChatView uses the same horizon
// (CHAT_SIGNED_URL_TTL_SECONDS), so a single mental model for the
// whole app: "files in private buckets get hour-long signed URLs."
// Signed URLs are ONLY minted for hashes missing from the local
// persistent photo cache (see src/lib/photoCache.ts) — every photo
// already in the cache renders from a local object URL with zero
// network requests.
const FAMILY_PHOTO_SIGNED_URL_TTL_SECONDS = 60 * 60;

/**
 * Cache-first photo URL resolver.
 *
 * The backend returns `family_photos` rows with a content hash
 * (migration 05002; legacy rows have hash = null). This hook:
 *
 *   1. For hash rows — checks the persistent local cache
 *      (IndexedDB, keyed by hash). Hits become object URLs
 *      immediately, no network.
 *   2. For misses and legacy rows — mints signed URLs in ONE batch
 *      (FamilyChatView's Bug #12 trick) for ONLY the missing
 *      objects, downloads the bytes, persists them into the local
 *      cache (hash rows), and object-URLs them.
 *
 * So the client never re-requests an image it already holds: the
 * only network traffic is for hashes absent from local storage,
 * plus legacy rows that predate content-addressing.
 *
 * Object URLs are revoked when a row disappears from the list or
 * the component unmounts (avoiding blob-url leaks).
 */
function usePhotoBlobUrls(
  rows: ReadonlyArray<Database["public"]["Tables"]["family_photos"]["Row"]>,
  familyId: string | null,
): Record<string, string> {
  const [urlMap, setUrlMap] = useState<Record<string, string>>({});
  // paths already resolved OR in-flight — guards against duplicate
  // work when `rows` identity changes but content doesn't.
  const handledRef = useRef<Set<string>>(new Set());
  // image_url path -> object URL we created (so we can revoke).
  const objectUrlsRef = useRef<Map<string, string>>(new Map());

  // Revoke object URLs for rows that disappeared (deleted) and on
  // unmount.
  useEffect(() => {
    const live = new Set(rows.map((r) => r.image_url));
    for (const [path, url] of objectUrlsRef.current) {
      if (!live.has(path)) {
        URL.revokeObjectURL(url);
        objectUrlsRef.current.delete(path);
        handledRef.current.delete(path); // allow re-resolve if re-added
      }
    }
  }, [rows]);
  useEffect(() => () => {
    for (const url of objectUrlsRef.current.values()) URL.revokeObjectURL(url);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!isAuthConfigured() || !familyId) return;
    const todo = rows.filter((r) => !handledRef.current.has(r.image_url));
    if (todo.length === 0) return;
    for (const r of todo) handledRef.current.add(r.image_url);

    (async () => {
      const updates: Array<[string, string]> = [];
      const hashRows = todo.filter((r) => r.hash != null);
      const legacyRows = todo.filter((r) => r.hash == null);

      // 1. Serve what we already have locally.
      const stillNeeded: typeof hashRows = [];
      await Promise.all(
        hashRows.map(async (r) => {
          if (cancelled) return;
          const blob = await getCachedPhoto(r.hash!);
          if (blob) {
            const url = URL.createObjectURL(blob);
            objectUrlsRef.current.set(r.image_url, url);
            updates.push([r.image_url, url]);
          } else {
            stillNeeded.push(r);
          }
        }),
      );

      // 2. Download ONLY the missing objects (one batch).
      const pathToRow = new Map<string, (typeof todo)[number]>();
      for (const r of stillNeeded) pathToRow.set(`${familyId}/${r.hash}`, r);
      for (const r of legacyRows) pathToRow.set(r.image_url, r);
      const paths = Array.from(pathToRow.keys());
      if (paths.length > 0) {
        const { data } = await supabase.storage
          .from("family-photos")
          .createSignedUrls(paths, FAMILY_PHOTO_SIGNED_URL_TTL_SECONDS);
        if (cancelled || !Array.isArray(data)) return;
        for (const entry of data) {
          if (!entry || !entry.path || !entry.signedUrl) continue;
          const row = pathToRow.get(entry.path);
          if (!row) continue;
          try {
            const blob = await fetch(entry.signedUrl).then((resp) => resp.blob());
            if (cancelled) return;
            if (row.hash) await putCachedPhoto(row.hash, blob);
            const url = URL.createObjectURL(blob);
            objectUrlsRef.current.set(row.image_url, url);
            updates.push([row.image_url, url]);
          } catch {
            // A single failed download shouldn't block the rest;
            // the row simply stays unresolved this pass.
          }
        }
      }

      if (!cancelled && updates.length > 0) {
        setUrlMap((prev) => {
          const out = { ...prev };
          for (const [path, url] of updates) out[path] = url;
          return out;
        });
      }
    })();

    return () => { cancelled = true; };
    // We intentionally do NOT key on urlMap: adding entries inside
    // setUrlMap(prev => ...) would re-trigger this effect. `rows`
    // + `familyId` are the only signals that need a re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, familyId]);

  return urlMap;
}

// Preset avatar gradient palette. Kept short on purpose — covers the
// common warm / cool / jewel tones the rest of the app already
// uses (see SearchPanel.tsx for the canonical set). When the user
// changes the colour we just write the Tailwind class string into
// `avatar_color`; the CSS class is opaque to the DB.
const AVATAR_PALETTE: { value: string; preview: string; label: string }[] = [
  { value: "from-rose-300 to-rose-600",     preview: "from-rose-300 to-rose-600",     label: "玫紅" },
  { value: "from-amber-300 to-amber-600",   preview: "from-amber-300 to-amber-600",   label: "琥珀" },
  { value: "from-emerald-300 to-emerald-600", preview: "from-emerald-300 to-emerald-600", label: "翠綠" },
  { value: "from-sky-300 to-sky-600",       preview: "from-sky-300 to-sky-600",       label: "天藍" },
  { value: "from-violet-300 to-violet-600", preview: "from-violet-300 to-violet-600", label: "紫丁" },
  { value: "from-pink-300 to-pink-600",     preview: "from-pink-300 to-pink-600",     label: "粉櫻" },
  { value: "from-teal-300 to-teal-600",     preview: "from-teal-300 to-teal-600",     label: "青碧" },
  { value: "from-zinc-300 to-zinc-600",     preview: "from-zinc-300 to-zinc-600",     label: "石墨" },
];

// Human-readable labels for the `family_relation` enum. The DB enum
// is the single source of truth; this map is the UI overlay. Same
// labels appear in the GenerationGroup headers, so the user sees
// the same vocabulary everywhere.
const RELATION_LABEL: Record<Rel, string> = {
  self:    "自己",
  spouse:  "配偶",
  parent:  "父母",
  child:   "子女",
  sibling: "兄弟姊妹",
  grand:   "祖父母",
  other:   "其他",
};
// Friendly hint that appears under the relation picker. Helps the
// user pick the right bucket — without it the enum values are
// opaque to non-English readers.
const RELATION_HINT: Record<Rel, string> = {
  self:    "你自己",
  spouse:  "丈夫 / 妻子",
  parent:  "爸爸 / 媽媽",
  child:   "仔女",
  sibling: "家姐 / 細佬",
  grand:   "公公 / 嫲嫲",
  other:   "其他親屬",
};

type Rel = "self" | "spouse" | "parent" | "child" | "sibling" | "grand" | "other";
type Node = {
  id: string;
  name: string;
  rel: Rel;
  initials: string;
  color: string;
  row: number;
  born?: string;
  /** Free-text relation label ("媽媽", "舅舅", …) from
   *  family_members.relation_label. Displayed as a small caption
   *  next to the display_name on the tree card. */
  relationLabel?: string | null;
  /** Public Storage URL of the user's profile picture (bucket
   *  `avatars`). Populated from `me.avatarUrl` for the signed-in
   *  user's own member card; null for other members. */
  avatarUrl?: string | null;
};

// Generation rows for the family tree:
//   grandparent (grand) → 0
//   parent          → 1
//   self / spouse / sibling  → 2 (same generation as the viewer)
//   child           → 3
// Anything we don't recognise (parents with a new `relation` value, or
// `relation` set NULL) stays in the peer row so it remains visible
// rather than silently dropping. The previous implementation filtered
// out every real member whose uuid didn't happen to match a static
// LAYOUT placeholder key, producing the "0 成員" regression.
const REL_TO_ROW: Record<Rel, number> = {
  self:    2,
  spouse:   2,
  sibling:  2,
  parent:   1,
  grand:    0,
  child:    3,
  other:    2,
};

function relFromString(s: string | null | undefined): Rel {
  switch ((s ?? "").trim()) {
    case "self":    return "self";
    case "spouse":  return "spouse";
    case "parent":  return "parent";
    case "child":   return "child";
    case "sibling": return "sibling";
    case "grand":   return "grand";
    default:        return "other";
  }
}

function GenerationGroup({
  row, label, nodes, onSelect,
}: {
  row: number;
  label: string;
  nodes: Node[];
  onSelect?: (id: string) => void;
}) {
  // Render every member whose row matches; the responsive grid
  // wraps the 6th, 7th, etc. instead of clipping them. No `col` field,
  // no LAYOUT placeholder, no fixed-width layout.
  const inRow = nodes.filter(n => n.row === row);
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
        <span className="ml-2 text-muted-foreground/70">
          ({inRow.length})
        </span>
      </div>
      <ul
        className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
      >
        {inRow.map(node => (
          <li key={node.id} className="flex justify-center">
            <button
              type="button"
              onClick={onSelect ? () => onSelect(node.id) : undefined}
              className="group flex w-full max-w-[120px] flex-col items-center gap-1.5 rounded-2xl border border-border/40 bg-card p-2.5 transition-colors hover:border-primary/40"
              aria-label={onSelect ? `編輯 ${node.name}（${label}）` : `${node.name}（${label}）`}
            >
              <MemberAvatar initials={node.initials} color={node.color} avatarUrl={node.avatarUrl} size="xl" />
              <span className="text-center">
                <span className="block truncate text-[11px] font-medium">{node.name}</span>
                {node.relationLabel && (
                  <span className="block truncate text-[9px] text-muted-foreground">{node.relationLabel}</span>
                )}
                {node.born && (
                  <span className="block text-[9px] text-muted-foreground">{node.born}</span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function FamTreeView() {
  const me0 = useCurrentUser();
  const cf  = useCurrentFamily(me0.user?.id ?? null);

  const [familyPhotoCount, setFamilyPhotoCount] = useState(0);
  const [familyStoryCount, setFamilyStoryCount] = useState(0);
  const [memoriesOpen, setMemoriesOpen] = useState(false);
  const [composerMode, setComposerMode] = useState<"photo" | "story">("photo");
  const [storyTitle, setStoryTitle] = useState("");
  const [storyBody, setStoryBody] = useState("");
  const [mediaMessage, setMediaMessage] = useState<string | null>(null);
  const [mediaSaving, setMediaSaving] = useState(false);
  const familyPhotoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    registerFab({ label: "新增家族記憶", icon: Plus, onClick: () => setMemoriesOpen(true) });
    return () => registerFab(null);
  }, []);

  const refreshFamilyMedia = async () => {
    if (!cf.family?.id || !isAuthConfigured()) return;
    const [photos, stories] = await Promise.all([
      supabase.from("family_photos").select("id", { count: "exact", head: true }).eq("family_id", cf.family.id),
      supabase.from("family_stories").select("id", { count: "exact", head: true }).eq("family_id", cf.family.id),
    ]);
    setFamilyPhotoCount(photos.count ?? 0);
    setFamilyStoryCount(stories.count ?? 0);
  };
  useEffect(() => { void refreshFamilyMedia(); }, [cf.family?.id]);

  const uploadFamilyPhoto = async (file: File) => {
    if (!cf.family?.id) return;
    if (file.size > 500 * 1024 * 1024) {
      setMediaMessage("照片太大，上限為 500 MB。");
      return;
    }
    setMediaMessage(null); setMediaSaving(true);
    try {
      // Content-addressed storage (migration 05002): the hash IS
      // the storage path, so identical uploads dedupe naturally and
      // the client can derive the path from the hash without the
      // server telling it. Seed the local persistent cache with the
      // bytes we already hold so the thumbnail renders instantly.
      const hash = await sha256Hex(file, file.lastModified);
      const path = `${cf.family.id}/${hash}`;
      const upload = await supabase.storage.from("family-photos").upload(path, file, { upsert: true });
      if (upload.error) { setMediaMessage(upload.error.message); return; }
      const { error } = await supabase.from("family_photos").upsert(
        { family_id: cf.family.id, member_id: cf.self?.id ?? null, image_url: path, hash, caption: null, taken_at: null },
        { onConflict: "family_id,hash", ignoreDuplicates: true },
      );
      if (error) {
        await supabase.storage.from("family-photos").remove([path]);
        setMediaMessage(error.message);
        return;
      }
      await putCachedPhoto(hash, file);
      await photos.refresh();
      await refreshFamilyMedia();
      setMediaMessage("照片已加入家族相簿。");
    } catch (e) {
      setMediaMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setMediaSaving(false);
    }
  };

  const saveFamilyStory = async () => {
    if (!cf.family?.id || !storyTitle.trim() || !storyBody.trim()) return;
    setMediaSaving(true); setMediaMessage(null);
    const { error } = await supabase.from("family_stories").insert({ family_id: cf.family.id, member_id: cf.self?.id ?? null, title: storyTitle.trim(), body: storyBody.trim(), recorded_at: null });
    if (error) setMediaMessage(error.message);
    else { setFamilyStoryCount(n => n + 1); setStoryTitle(""); setStoryBody(""); setMediaMessage("故事已保存。"); }
    setMediaSaving(false);
  };

  // Crest upload lifecycle. Lives in a hook so the rev/changed
  // semantics (`compressing → uploading → saving → done`) are
  // isolated from the rest of the view and so the same hook
  // could be reused on a "change crest" sheet later without
  // re-deriving the state machine.
  const crestUpload = useCrestUpload();

  // ───────────────────────  Family memories library (回憶庫)  ─────
  // The 家族記憶 card only had an *upload* affordance, so once a
  // user added a few photos and stories there was no way to view
  // them again — only the count surfaced in the 家族概覽 stats.
  // This block surfaces both `family_photos` and `family_stories`
  // with realtime refresh, signed-URL resolution for the photo
  // thumbnails (bucket is private; RLS scopes reads to family),
  // and an M3BottomSheet that lets the author edit or delete
  // their own stories. RLS already gates `family_stories` updates
  // to `member_id = auth.uid()'s member id`, so the action
  // server-side rejects writes from non-authors; we mirror that
  // here by only rendering the trash button to the author.
  type FamilyPhotoRow  = Database["public"]["Tables"]["family_photos"]["Row"];
  type FamilyStoryRow  = Database["public"]["Tables"]["family_stories"]["Row"];
  type MemoriesTab = "photos" | "stories";
  const [memoriesTab, setMemoriesTab] = useState<MemoriesTab>("photos");
  const [lightbox, setLightbox] = useState<LightboxMedia | null>(null);
  const [viewingStory, setViewingStory] = useState<FamilyStoryRow | null>(null);
  const [editingStory, setEditingStory] = useState<FamilyStoryRow | null>(null);
  const [storyEditTitle, setStoryEditTitle] = useState("");
  const [storyEditBody, setStoryEditBody] = useState("");
  const [storyActionError, setStoryActionError] = useState<string | null>(null);
  const [storySaving, setStorySaving] = useState(false);

  const photos = useSupabaseQuery<FamilyPhotoRow[]>(async () => {
    if (!isAuthConfigured() || !cf.family?.id) return { data: [], error: null };
    return await supabase
      .from("family_photos")
      .select("*")
      .eq("family_id", cf.family.id)
      .order("created_at", { ascending: false });
  }, [cf.family?.id]);

  useRealtimeTable<"family_photos">(
    "family_photos",
    cf.family?.id ? `family_id=eq.${cf.family.id}` : null,
    () => photos.refresh(),
  );

  const stories = useSupabaseQuery<FamilyStoryRow[]>(async () => {
    if (!isAuthConfigured() || !cf.family?.id) return { data: [], error: null };
    return await supabase
      .from("family_stories")
      .select("*")
      .eq("family_id", cf.family.id)
      .order("created_at", { ascending: false });
  }, [cf.family?.id]);

  useRealtimeTable<"family_stories">(
    "family_stories",
    cf.family?.id ? `family_id=eq.${cf.family.id}` : null,
    () => stories.refresh(),
  );

  // `family_photos.image_url` stores a STORAGE PATH (not a public
  // URL — the bucket is private, family-scoped RLS). Resolve signed
  // URLs in a single batched `createSignedUrls` call so we don't
  // pay one HTTP round-trip per thumbnail. Cache by path; on a new
  // photo we add its entry, on a deletion it's dropped from the
  // map on the next refresh. The same helper that FamilyChatView
  // uses; we just key off the `image_url` path directly because
  // family photos are static once uploaded (no streaming / GIF /
  // video formats that change once rendered).
  const photoUrls = usePhotoBlobUrls(photos.data ?? [], cf.family?.id ?? null);

  // Photo tap → lightbox. The MediaLightbox handles its own
  // AnimatePresence + ESC/backdrop close, so the caller only
  // toggles `lightbox` between `null` and a media record.
  const openPhotoLightbox = (row: FamilyPhotoRow) => {
    const url = photoUrls[row.image_url];
    if (!url) return;
    const alt = row.caption?.trim() || `家族相簿 #${row.id.slice(0, 6)}`;
    setLightbox({ kind: "image", url, alt });
  };

  const closeStoryView = () => {
    setViewingStory(null);
    setStoryActionError(null);
  };
  const openStoryEditor = (row: FamilyStoryRow) => {
    setStoryEditTitle(row.title);
    setStoryEditBody(row.body);
    setStoryActionError(null);
    setEditingStory(row);
  };
  const closeStoryEditor = () => {
    setEditingStory(null);
    setStoryActionError(null);
  };
  const saveStoryEdit = async () => {
    if (!editingStory) return;
    const title = storyEditTitle.trim();
    const body  = storyEditBody.trim();
    if (!title || !body) {
      setStoryActionError("標題同內容都唔可以空白。");
      return;
    }
    setStorySaving(true);
    setStoryActionError(null);
    const upd = await supabase
      .from("family_stories")
      .update({ title, body })
      .eq("id", editingStory.id);
    if (upd.error) {
      setStoryActionError(`儲存失敗：${upd.error.message}`);
      setStorySaving(false);
      return;
    }
    await stories.refresh();
    setStorySaving(false);
    setEditingStory(null);
    // If the user was viewing the same story, refresh the read sheet
    // so the new content shows without a second round-trip.
    if (viewingStory && viewingStory.id === editingStory.id) {
      const refreshed = (stories.data ?? []).find((r) => r.id === editingStory.id) ?? null;
      setViewingStory(refreshed);
    }
  };
  const deleteStory = async (row: FamilyStoryRow) => {
    if (typeof window !== "undefined"
        && !window.confirm(`確定刪除「${row.title}」呢個故事？`)) {
      return;
    }
    setStoryActionError(null);
    const del = await supabase.from("family_stories").delete().eq("id", row.id);
    if (del.error) {
      setStoryActionError(`刪除失敗：${del.error.message}`);
      return;
    }
    await stories.refresh();
    if (viewingStory?.id === row.id) setViewingStory(null);
  };

  const deletePhoto = async (row: FamilyPhotoRow) => {
    if (typeof window !== "undefined" && !window.confirm("確定刪除呢張照片？")) return;
    setMediaMessage(null);
    const del = await supabase.from("family_photos").delete().eq("id", row.id);
    if (del.error) { setMediaMessage(`刪除失敗：${del.error.message}`); return; }
    await supabase.storage.from("family-photos").remove([row.image_url]);
    if (row.hash) await deleteCachedPhoto(row.hash); // evict from local cache
    await photos.refresh();
    setFamilyPhotoCount(n => Math.max(0, n - 1));
    setLightbox(null);
  };

  // Helper: is the active viewer the story's author? Mirrors
  // `family_stories_author_delete` (migration 014):
  //   `member_id in (select id from family_members where user_id = auth.uid())`
  // — the same condition that gates UPDATE and DELETE server-side,
  // so the trash button renders correctly even on a stale row.
  const isStoryOwner = (row: FamilyStoryRow) =>
    !!cf.self?.id && row.member_id != null && row.member_id === cf.self.id;
  const isPhotoOwner = (row: FamilyPhotoRow) =>
    !!cf.self?.id && row.member_id != null && row.member_id === cf.self.id;
  // Local file picker state — `pending` is the File the user
  // chose but hasn't confirmed yet, `pendingUrl` is the object
  // URL we render in the preview circle. We don't push the file
  // straight into the hook on `change` because the user might
  // pick the wrong image and want to swap without uploading.
  const crestFileInputRef = useRef<HTMLInputElement>(null);
  const [pendingCrestFile, setPendingCrestFile] = useState<File | null>(null);
  const [pendingCrestUrl, setPendingCrestUrl] = useState<string | null>(null);
  const crestName = cf.family?.name ?? "";

  // Family-name editing. The header <h1> swaps between read-mode
  // (showing the name + a pencil button when the caller is
  // allowed to edit) and edit-mode (an input with commit-on-Enter
  // / commit-on-blur / Escape-cancels). The save calls
  // `families_admin_update` RLS, which gates on
  // is_family_admin_of(id). `isCreator` is the local proxy for
  // that; rlsMessage surfaces the error if the PATCH is denied.
  const [editingFamilyName, setEditingFamilyName] = useState(false);
  const [familyNameDraft, setFamilyNameDraft]       = useState<string>("");
  const [familyNameSaving,  setFamilyNameSaving]    = useState(false);
  const [familyNameError,   setFamilyNameError]     = useState<string | null>(null);

  const startEditingFamilyName = () => {
    if (!isCreator || !cf.family) return;
    setFamilyNameDraft(cf.family.name);
    setFamilyNameError(null);
    setEditingFamilyName(true);
  };
  const cancelEditingFamilyName = () => {
    if (familyNameSaving) return;
    setEditingFamilyName(false);
    setFamilyNameDraft("");
    setFamilyNameError(null);
  };
  const saveFamilyName = async () => {
    if (!cf.family) return;
    const next = familyNameDraft.trim();
    // Mirror the DB CHECK (char_length between 1 and 80) on the
    // client so an out-of-range name is caught before we burn a
    // round-trip; rlsMessage() below doesn't know the column rule.
    if (next.length < 1 || next.length > 80) {
      setFamilyNameError("家庭名稱要 1 - 80 字。");
      return;
    }
    if (next === cf.family.name) {
      // No-op commit — close the editor without a PATCH.
      setEditingFamilyName(false);
      setFamilyNameDraft("");
      return;
    }
    setFamilyNameSaving(true);
    setFamilyNameError(null);
    const { error } = await supabase
      .from("families")
      .update({ name: next })
      .eq("id", cf.family.id);
    setFamilyNameSaving(false);
    if (error) {
      setFamilyNameError(rlsMessage(error));
      return;
    }
    setEditingFamilyName(false);
    setFamilyNameDraft("");
    // Re-query the families row so the new name flows into the
    // pageTitle / pageSubtitle slots used by TodayView, FamList,
    // ModuleMembersView, the AppShell desktop top bar, etc.
    cf.refresh();
  };

  // Edit sheet. Any non-null id opens the sheet in edit mode for
  // that member; null means the sheet is closed. FamTree is
  // edit-only: there is no create path here. Real new linked
  // members come from ModuleMembersView (email lookup +
  // assign_existing_user_to_family).
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  // Surface save errors on the edit sheet; the row's
  // useRealtimeTable refresh re-renders the tree once Supabase
  // broadcasts the change.
  const [editError, setEditError] = useState<string | null>(null);

  // Is the current user the family creator? The per-card edit
  // sheet uses this to gate the form (creator can edit any row;
  // a regular member can only edit their own). RLS on
  // family_members is the real gate; the UI just hides controls
  // early to avoid silent-fail taps.
  const isCreator =
    Boolean(me0.user?.id) &&
    Boolean(cf.family?.created_by) &&
    cf.family?.created_by === me0.user?.id;

  // Re-render the tree when family_members changes (live). The
  // hook is a no-op if the user is logged out, so we guard.
  useRealtimeTable<"family_members">(
    "family_members",
    null,
    () => cf.refresh(),
  );

  // Build nodes from cf.members, every member included. `row` is
  // derived from the relational place (peer row for unknown rels so
  // nothing disappears); the responsive grid takes care of wrap.
  const nodes: Node[] = useMemo(() => {
    // The signed-in user's family_members row (cf.self) is the
    // link between the global profile and this family. Whenever
    // a member's user_id matches the signed-in user, surface the
    // global avatar so the tree picks up the photo the user set
    // from the Settings sheet without an extra query.
    const selfMemberId = cf.self?.id ?? null;
    const selfUserId   = me0.user?.id ?? null;
    return cf.members
      .slice()
      .sort((a, b) => {
        const at = (a as unknown as { created_at?: string }).created_at ?? "";
        const bt = (b as unknown as { created_at?: string }).created_at ?? "";
        return at < bt ? -1 : at > bt ? 1 : 0;
      })
      .map((m): Node => {
        const isSelf = selfMemberId != null
          && m.id === selfMemberId
          && selfUserId != null;
        return {
          id: m.id,
          name: m.display_name,
          rel: relFromString(m.relation),
          // `row` is the only layout field; the grid wraps freely beyond
          // any fixed column count.
          row: REL_TO_ROW[relFromString(m.relation)],
          initials: m.initials,
          color: m.avatar_color,
          born: undefined,
          relationLabel: m.relation_label ?? null,
          avatarUrl: isSelf ? (me0.avatarUrl ?? null) : null,
        };
      });
  }, [cf.members, cf.self?.id, me0.user?.id, me0.avatarUrl]);

  // Manage the local-only object URL lifetime for the crest
  // preview. We don't keep the URL across renders when no file
  // is selected, and we revoke on cleanup so an unmounted
  // component never leaks an object URL.
  useEffect(() => {
    if (!pendingCrestFile) {
      setPendingCrestUrl(null);
      return;
    }
    const url = URL.createObjectURL(pendingCrestFile);
    setPendingCrestUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingCrestFile]);

  // After an upload settles (success, error, or idle), clear the
  // pending file so the file picker resets and the preview
  // collapses back to whatever the server returned. Without
  // this, the user could be left staring at a stale local preview
  // that doesn't match the persisted row.
  useEffect(() => {
    if (
      crestUpload.state.phase === "done"
      || crestUpload.state.phase === "error"
      || crestUpload.state.phase === "idle"
    ) {
      setPendingCrestFile(null);
      if (crestFileInputRef.current) {
        crestFileInputRef.current.value = "";
      }
    }
  }, [crestUpload.state]);

  // Right-rail context for `xl+` (wide desktop). A member legend
  // grouped by generation row (grand / parent / self / child),
  // mirroring the layout of the main tree so the rail acts as
  // a quick reference. Re-built when the underlying data shifts.
  const groupedByRow = useMemo(() => {
    const order: Rel[] = ["grand", "parent", "self", "sibling", "spouse", "other", "child"];
    const labels: Record<Rel, string> = {
      grand: "祖父母", parent: "父母", self: "自己",
      sibling: "兄弟姊妹", spouse: "配偶", other: "其他", child: "子女",
    };
    const out: { row: Rel; label: string; nodes: Node[] }[] = [];
    for (const row of order) {
      const group = nodes.filter((n) => n.rel === row);
      if (group.length > 0) out.push({ row, label: labels[row], nodes: group });
    }
    return out;
  }, [nodes]);
  // Memoize the right-rail slot. Passing an inline object (the
  // previous shape) made `usePublishModuleSlot` see a fresh
  // reference every render, which its effect treated as "changed"
  // and called Provider.setSlot — feeding a `setSlot → Provider
  // re-render → ...` cycle that React surfaces as "Maximum
  // update depth exceeded" (prod 2026-08-04). TodayView / FamList-
  // View already wrap their slot in useMemo; match that pattern.
  usePublishModuleSlot(useMemo(() => ({
    pageTitle: cf.family?.name ?? "FamTree",
    pageSubtitle: cf.family?.name ? `${cf.family.name} 家徽` : undefined,
    context: (
      <div className="flex flex-col gap-3">
        <M3Card className="border-border/40">
          <M3CardHeader
            title="家徽"
            icon={
              cf.crest?.image_url ? (
                <span
                  aria-hidden
                  className="grid h-7 w-7 shrink-0 place-items-center overflow-hidden rounded-full ring-1 ring-border/60"
                >
                  <img
                    src={cf.crest.image_url}
                    alt=""
                    className="h-full w-full object-cover"
                    key={cf.crest.image_url}
                  />
                </span>
              ) : (
                <Crown className="h-4 w-4" aria-hidden />
              )
            }
            trailing={cf.family?.name ?? "未設定"}
          />
          <p className="mt-2 text-[11px] text-muted-foreground">
            你家嘅家徽，會顯示喺 FamTree 嘅標題旁。
          </p>
        </M3Card>
        <M3Card className="border-border/40">
          <M3CardHeader
            title="成員分佈"
            icon={<Users className="h-4 w-4" aria-hidden />}
            trailing={`${nodes.length} 人`}
          />
          <Stagger as="ul" className="mt-3 space-y-2" gap={0.04}>
            {groupedByRow.map(({ row, label, nodes: group }) => (
              <StaggerItem key={row} as="li">
                <div className="rounded-xl bg-secondary/30 p-2.5">
                  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {label} · {group.length}
                  </div>
                  <ul className="space-y-1">
                    {group.map((n) => (
                      <li key={n.id} className="flex items-center gap-2 text-sm">
                        <MemberAvatar initials={n.initials} color={n.color} avatarUrl={n.avatarUrl} size="sm" />
                        <span className="truncate text-foreground/85">{n.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </M3Card>
      </div>
    ),
  }), [cf.family?.name, cf.crest?.image_url, nodes.length, groupedByRow]));
  return (
    <div className="space-y-4 sm:space-y-6">
      <motion.header
        className="px-1 pt-1"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      >
        <p className="text-xs text-muted-foreground sm:text-sm">FamTree · 家族樹</p>
        <div className="mt-1 flex items-center gap-2">
          {editingFamilyName ? (
            <input
              autoFocus
              value={familyNameDraft}
              onChange={(e) => setFamilyNameDraft(e.currentTarget.value)}
              onBlur={() => { if (!familyNameSaving) void saveFamilyName(); }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  (e.currentTarget as HTMLInputElement).blur();
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  cancelEditingFamilyName();
                }
              }}
              disabled={familyNameSaving}
              maxLength={80}
              // Font metrics mirror the read-mode <h1> so toggling
              // modes doesn't shift the layout.
              className="font-serif flex-1 bg-transparent text-2xl font-semibold tracking-tight outline-none border-b-2 border-primary sm:text-3xl lg:text-4xl"
            />
          ) : (
            <>
              <h1 className="font-serif text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
                {cf.family?.name ?? "你的家"}
              </h1>
              {/*
                Pencil affordance — only renders for the family
                creator / RLS-allowed updater. Always visible
                rather than hover-only because touch users wouldn't
                otherwise see it.
              */}
              {isCreator && cf.family && (
                <button
                  type="button"
                  onClick={startEditingFamilyName}
                  aria-label="編輯家庭名稱"
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground active:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </button>
              )}
            </>
          )}
          {editingFamilyName && familyNameSaving && (
            <span role="status" className="shrink-0 text-[11px] text-muted-foreground">
              儲存中…
            </span>
          )}
        </div>
        {familyNameError && (
          <p
            role="alert"
            className="mt-2 text-xs text-rose-700"
          >
            {familyNameError}
          </p>
        )}
        <p className="mt-2 text-pretty text-sm text-muted-foreground sm:text-base">
          灌溉你的家族樹，串起每一份關係。
        </p>
      </motion.header>

      {/* Crest editor */}
      <M3Card>
        <CrestEditor
          familyId={cf.family?.id ?? null}
          crestName={cf.family?.name ?? ""}
          imageUrl={cf.crest?.image_url ?? null}
          pendingPreviewUrl={pendingCrestUrl}
          hasPendingFile={!!pendingCrestFile}
          uploadState={crestUpload.state}
          fileInputRef={crestFileInputRef}
          onPickFile={() => crestFileInputRef.current?.click()}
          onChooseFile={(file) => setPendingCrestFile(file)}
          onCancelPending={() => {
            setPendingCrestFile(null);
            if (crestFileInputRef.current) crestFileInputRef.current.value = "";
          }}
          onConfirmPending={() => {
            if (!pendingCrestFile || !cf.family?.id) return;
            void crestUpload.upload(cf.family.id, pendingCrestFile).then((res) => {
              if (res) cf.refresh();
            });
          }}
          onRemove={() => {
            if (!cf.family?.id) return;
            if (typeof window !== "undefined"
                && !window.confirm("確定移除家徽？之後會用返預設圖示。")) return;
            void crestUpload.remove(cf.family.id).then((ok) => {
              if (ok) cf.refresh();
            });
          }}
        />
        <p className="mt-2 text-[11px] text-muted-foreground">
          家徽會顯示喺 FamChat 群組頭像同 FamTree 標題。
          上限 5 MiB、支援 PNG / JPEG / WebP。
        </p>
      </M3Card>

      {/* Quick stats */}
      <M3Card>
        <M3CardHeader title="家族概覽" />
        <Stagger as="ul" className="grid grid-cols-3 gap-2 sm:gap-3" gap={0.05}>
          <StaggerItem as="li" className="rounded-2xl border border-border/40 bg-card p-3 text-center">
            <div className="font-serif text-2xl font-semibold">{nodes.length}</div>
            <div className="text-[10px] text-muted-foreground">成員</div>
          </StaggerItem>
          <StaggerItem as="li" className="rounded-2xl border border-border/40 bg-card p-3 text-center">
            <div className="font-serif text-2xl font-semibold">{familyPhotoCount}</div>
            <div className="text-[10px] text-muted-foreground">舊照片</div>
          </StaggerItem>
          <StaggerItem as="li" className="rounded-2xl border border-border/40 bg-card p-3 text-center">
            <div className="font-serif text-2xl font-semibold">{familyStoryCount}</div>
            <div className="text-[10px] text-muted-foreground">故事</div>
          </StaggerItem>
        </Stagger>
      </M3Card>

      <AnimatePresence>
        {memoriesOpen && (
          <M3BottomSheet title="家族記憶" onClose={() => setMemoriesOpen(false)}>
            <div>
              <div role="tablist" aria-label="新增回憶類型" className="grid grid-cols-2 rounded-full bg-secondary/50 p-1">
                <button type="button" role="tab" aria-selected={composerMode === "photo"} onClick={() => setComposerMode("photo")} className={cn("min-h-12 rounded-full px-3 py-2 text-sm transition-colors", composerMode === "photo" && "bg-card font-medium shadow-sm")}><ImageIcon className="mr-1.5 inline h-4 w-4" />上傳照片</button>
                <button type="button" role="tab" aria-selected={composerMode === "story"} onClick={() => setComposerMode("story")} className={cn("min-h-12 rounded-full px-3 py-2 text-sm transition-colors", composerMode === "story" && "bg-card font-medium shadow-sm")}><BookOpen className="mr-1.5 inline h-4 w-4" />寫下故事</button>
              </div>
        <div className="mt-3">
          <div className={cn("rounded-2xl border border-border/40 p-4", composerMode !== "photo" && "hidden")}>
            <div className="flex items-center gap-2 font-medium"><ImageIcon className="h-4 w-4" />家族相簿</div>
            <p className="mt-1 text-xs text-muted-foreground">上傳一張照片，留低珍貴回憶。</p>
            <input ref={familyPhotoInputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) void uploadFamilyPhoto(file); e.currentTarget.value = ""; }} />
            <Button className="mt-3 h-12 w-full" variant="outline" disabled={mediaSaving} onClick={() => familyPhotoInputRef.current?.click()}><Upload className="mr-2 h-4 w-4" />上傳照片</Button>
          </div>
          <div className={cn("rounded-2xl border border-border/40 p-4", composerMode !== "story" && "hidden")}>
            <div className="flex items-center gap-2 font-medium"><BookOpen className="h-4 w-4" />寫下故事</div>
            <input value={storyTitle} onChange={e => setStoryTitle(e.target.value)} maxLength={120} placeholder="故事標題" className="mt-3 w-full rounded-xl border bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            <textarea value={storyBody} onChange={e => setStoryBody(e.target.value)} maxLength={8000} placeholder="分享一段家族回憶…" rows={3} className="mt-2 w-full resize-none rounded-xl border bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            <Button className="mt-2 h-12 w-full" disabled={mediaSaving || !storyTitle.trim() || !storyBody.trim()} onClick={() => void saveFamilyStory()}><Plus className="mr-2 h-4 w-4" />保存故事</Button>
          </div>
        </div>
        {mediaMessage && <p role="status" className="mt-3 text-xs text-muted-foreground">{mediaMessage}</p>}
      </div>
          </M3BottomSheet>
        )}
      </AnimatePresence>

      {/* ─────────────────────  回憶庫 (memories library)  ─────── */}
      <M3Card>
        <M3CardHeader
          title="家族回憶庫"
          subtitle="重溫已保存嘅舊照片同故事"
        />
        {/* Tab switcher — segmented pills (M3). The Photos / Stories
            split mirrors the two add affordances above so the user's
            mental model matches: save via the form, view via the tab
            below. Photos is the default — it's the more visually
            scannable surface when the family has many items. */}
        <div
          role="tablist"
          aria-label="回憶庫分類"
          className="mt-3 inline-flex rounded-full border border-border/60 bg-secondary/30 p-1 text-xs"
        >
          {([
            ["photos",  `相簿 (${photos.data?.length ?? 0})`,  ImageIcon],
            ["stories", `故事 (${stories.data?.length ?? 0})`, ScrollText],
          ] as const).map(([key, label, Icon]) => {
            const active = memoriesTab === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setMemoriesTab(key)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 transition-colors",
                  active
                    ? "bg-card text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {label}
              </button>
            );
          })}
        </div>

        <div className="mt-3">
          {memoriesTab === "photos" ? (
            (photos.data ?? []).length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border/60 p-6 text-center text-xs text-muted-foreground">
                仲未有任何照片。撳上面「上傳照片」加入一張。
              </div>
            ) : (
              <ul className="grid grid-cols-3 gap-1.5 sm:gap-2">
                {(photos.data ?? []).map((p) => {
                  const url = photoUrls[p.image_url];
                  const member = p.member_id
                    ? cf.members.find((m) => m.id === p.member_id) ?? null
                    : null;
                  return (
                    <li key={p.id} className="relative">
                      <button
                        type="button"
                        onClick={() => openPhotoLightbox(p)}
                        aria-label={p.caption?.trim() || "舊照片"}
                        className="group relative block aspect-square w-full overflow-hidden rounded-xl border border-border/40 bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                      >
                        {url ? (
                          <img
                            src={url}
                            alt={p.caption ?? "家族舊照片"}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]"
                          />
                        ) : (
                          <div className="grid h-full w-full place-items-center text-muted-foreground">
                            <ImageIcon className="h-5 w-5" aria-hidden />
                          </div>
                        )}
                        {member && (
                          <span
                            aria-hidden
                            className="absolute bottom-1 right-1 grid h-5 w-5 place-items-center rounded-full bg-black/55 text-[10px] font-semibold text-white ring-1 ring-white/30"
                          >
                            {member.initials}
                          </span>
                        )}
                      </button>
                      {isPhotoOwner(p) && (
                        <button
                          type="button"
                          aria-label="刪除照片"
                          onClick={() => void deletePhoto(p)}
                          className="absolute right-1 top-1 grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white shadow-sm transition-colors hover:bg-destructive"
                        >
                          <Trash2 className="h-4 w-4" aria-hidden />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )
          ) : (stories.data ?? []).length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border/60 p-6 text-center text-xs text-muted-foreground">
              仲未有任何故事。撳上面「保存故事」記低一段回憶。
            </div>
          ) : (
            <Stagger as="ul" className="space-y-2" gap={0.04}>
              {(stories.data ?? []).map((s) => {
                const member = s.member_id
                  ? cf.members.find((m) => m.id === s.member_id) ?? null
                  : null;
                return (
                  <StaggerItem key={s.id} as="li">
                    <button
                      type="button"
                      onClick={() => setViewingStory(s)}
                      className="flex w-full flex-col gap-1 rounded-2xl border border-border/40 bg-card p-3 text-left transition-colors hover:border-border hover:bg-secondary/30 active:scale-[0.99]"
                    >
                      <div className="flex items-center gap-2">
                        {member ? (
                          <MemberAvatar
                            initials={member.initials}
                            color={member.avatar_color}
                            avatarUrl={member.id === cf.self?.id ? (me0.avatarUrl ?? null) : null}
                            size="sm"
                          />
                        ) : (
                          <span
                            aria-hidden
                            className="grid h-7 w-7 place-items-center rounded-full bg-secondary text-foreground/70"
                          >
                            <ScrollText className="h-4 w-4" aria-hidden />
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium">{s.title}</div>
                          <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                            {member ? <span className="truncate">{member.display_name}</span> : <span>家人</span>}
                            <span aria-hidden>·</span>
                            <span className="inline-flex items-center gap-0.5">
                              <Calendar className="h-3 w-3" aria-hidden />
                              {formatStoryDate(s.recorded_at ?? s.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                      {s.body && (
                        <p className="line-clamp-2 whitespace-pre-line pl-9 text-xs leading-relaxed text-foreground/80">
                          {s.body}
                        </p>
                      )}
                    </button>
                  </StaggerItem>
                );
              })}
            </Stagger>
          )}
        </div>
        {(photos.error || stories.error) && (
          <p role="alert" className="mt-3 text-xs text-destructive">
            {photos.error ?? stories.error}
          </p>
        )}
      </M3Card>

      <M3Card>
        <M3CardHeader
          title="家族關係"
          subtitle="點擊成員查看或編輯"
        />
        <div className="mt-3 space-y-4">
          <GenerationGroup
            row={0}
            label="祖父母"
            nodes={nodes}
            onSelect={setEditingMemberId}
          />
          <GenerationGroup
            row={1}
            label="父母"
            nodes={nodes}
            onSelect={setEditingMemberId}
          />
          <GenerationGroup
            row={2}
            label="同輩"
            nodes={nodes}
            onSelect={setEditingMemberId}
          />
          <GenerationGroup
            row={3}
            label="子女"
            nodes={nodes}
            onSelect={setEditingMemberId}
          />
        </div>
      </M3Card>

      <p className="px-2 pt-2 text-center text-[11px] leading-relaxed text-muted-foreground">
        <Sprout className="mr-1 inline h-3 w-3" aria-hidden />
        點擊成員設定佢嘅輩分，或者用「設定關係」一次過揀。
      </p>
      <MemberEditSheet
        member={
          editingMemberId
            ? cf.members.find((m) => m.id === editingMemberId) ?? null
            : null
        }
        isCreator={isCreator}
        isSelf={Boolean(cf.self?.id && editingMemberId === cf.self.id)}
        globalAvatarUrl={me0.avatarUrl}
        familyId={cf.family?.id ?? null}
        error={editError}
        onError={setEditError}
        onClose={() => {
          setEditingMemberId(null);
          setEditError(null);
        }}
        onSaved={() => {
          setEditingMemberId(null);
          setEditError(null);
          cf.refresh();
        }}
      />

      {/* Photo lightbox — the existing MediaLightbox owns its own
          AnimatePresence + ESC + backdrop close, so we just flip
          `lightbox` between null and a media record. */}
      <MediaLightbox media={lightbox} onClose={() => setLightbox(null)} />

      {/* Read-only view for a story. Always reachable from any
          member; the trash / edit pencil show only for the author
          (matching RLS family_stories_author_{update,delete}). */}
      <AnimatePresence>
        {viewingStory && !editingStory && (
          <M3BottomSheet
            onClose={closeStoryView}
            title={viewingStory.title}
            className="max-h-[92vh]"
          >
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {(() => {
                  const m = viewingStory.member_id
                    ? cf.members.find((mm) => mm.id === viewingStory.member_id) ?? null
                    : null;
                  if (!m) return <span>家人</span>;
                  return (
                    <>
                      <MemberAvatar
                        initials={m.initials}
                        color={m.avatar_color}
                        avatarUrl={m.id === cf.self?.id ? (me0.avatarUrl ?? null) : null}
                        size="sm"
                      />
                      <span className="font-medium text-foreground/85">{m.display_name}</span>
                    </>
                  );
                })()}
                <span aria-hidden>·</span>
                <span className="inline-flex items-center gap-0.5">
                  <Calendar className="h-3 w-3" aria-hidden />
                  {formatStoryDate(viewingStory.recorded_at ?? viewingStory.created_at)}
                </span>
              </div>
              <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/90">
                {viewingStory.body}
              </p>
              {storyActionError && (
                <p role="alert" className="text-xs text-destructive">{storyActionError}</p>
              )}
              {isStoryOwner(viewingStory) && (
                <div className="flex items-center justify-between gap-2 border-t border-border/60 pt-3">
                  <button
                    type="button"
                    onClick={() => void deleteStory(viewingStory)}
                    className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive active:bg-destructive/20"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    刪除呢個故事
                  </button>
                  <button
                    type="button"
                    onClick={() => openStoryEditor(viewingStory)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3.5 py-1.5 text-xs font-medium text-primary-foreground active:scale-[0.98]"
                  >
                    <Pencil className="h-3.5 w-3.5" aria-hidden />
                    編輯
                  </button>
                </div>
              )}
            </div>
          </M3BottomSheet>
        )}
      </AnimatePresence>

      {/* Edit sheet — only reachable from the read sheet above (we
          check `editingStory`, not `viewingStory`), so the user
          always sees the read view first. Closing the edit sheet
          pops back to the read view if `viewingStory` is still
          set, or to nothing if the read view was already closed. */}
      <AnimatePresence>
        {editingStory && (
          <M3BottomSheet
            onClose={closeStoryEditor}
            title="編輯故事"
            className="max-h-[92vh]"
          >
            <div className="space-y-3">
              <div>
                <label
                  htmlFor="story-edit-title"
                  className="text-[11px] font-medium text-muted-foreground"
                >
                  標題
                </label>
                <input
                  id="story-edit-title"
                  value={storyEditTitle}
                  onChange={(e) => setStoryEditTitle(e.currentTarget.value)}
                  maxLength={120}
                  className="mt-1 h-11 w-full rounded-2xl border border-border/60 bg-background px-3.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                />
              </div>
              <div>
                <label
                  htmlFor="story-edit-body"
                  className="flex items-center justify-between text-[11px] font-medium text-muted-foreground"
                >
                  <span>內容</span>
                  <span className="tabular-nums">{storyEditBody.length} / 8000</span>
                </label>
                <textarea
                  id="story-edit-body"
                  value={storyEditBody}
                  onChange={(e) => setStoryEditBody(e.currentTarget.value.slice(0, 8000))}
                  rows={8}
                  className="mt-1 block w-full resize-y rounded-2xl border border-border/60 bg-background px-3.5 py-3 text-sm leading-relaxed focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                />
              </div>
              {storyActionError && (
                <p role="alert" className="text-xs text-destructive">{storyActionError}</p>
              )}
              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeStoryEditor}
                  className="h-10 rounded-full border border-border/60 bg-card px-4 text-sm"
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={() => void saveStoryEdit()}
                  disabled={storySaving || !storyEditTitle.trim() || !storyEditBody.trim()}
                  className="h-10 rounded-full bg-primary px-5 text-sm font-medium text-primary-foreground disabled:opacity-50"
                >
                  {storySaving ? "儲存中…" : "儲存"}
                </button>
              </div>
            </div>
          </M3BottomSheet>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Format a story's date (recorded_at if set, otherwise created_at)
 * as a YYYY-MM-DD string. We don't use `toLocaleDateString` here
 * because the family's primary language is zh-Hant, and the family
 * app already shows absolute dates in YYYY-MM-DD elsewhere (the
 * chat-attachments column metadata, the check-in scheduler, etc.).
 * Tweak to whatever the design system settles on.
 */
function formatStoryDate(d: string): string {
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return d;
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return d;
  }
}

/* ─────────────────── MemberEditSheet ───────────────────
 *
 * Edit / add sheet for a single family_members row.
 *
 * RLS contract (mirrored on the DB side by 01400):
 *   • CREATE   : only the family creator (`family.created_by =
 *                auth.uid()`) can INSERT. The user can also
 *                INSERT their own membership row directly
 *                (e.g. on bootstrap), but for a tree edit flow
 *                the creator is the only realistic caller.
 *   • UPDATE   : creator can update any row; everyone else
 *                can only update their own. `user_id` and
 *                `family_id` are NOT exposed in the form —
 *                they're immutable for non-creators.
 *   • DELETE   : same as UPDATE. For non-creators, only their
 *                own row.
 *
 * The component is open even when the user has no RLS rights
 * on the row (the underlying cf.members row exists because
 * it's already in the user's family), but the form is
 * downgraded to view-only with a "你只可以編輯自己嘅成員
 * 資料" notice. This avoids the silent-fail UX where a user
 * edits a field, taps Save, and the upsert returns 401
 * without the page telling them why.
 *
 * The DB trigger `prevent_family_change` raises on any
 * attempt to change `family_id`; we don't even pass that
 * field to the .update() call. `created_at` and `id` are
 * also off-limits.
 */
type FamilyMemberRow = Database["public"]["Tables"]["family_members"]["Row"];

function MemberEditSheet({
  member,
  isCreator,
  isSelf,
  globalAvatarUrl,
  familyId,
  error,
  onError,
  onClose,
  onSaved,
}: {
  member: FamilyMemberRow | null;
  isCreator: boolean;
  isSelf: boolean;
  /** Public Storage URL of the signed-in user's global avatar
   *  (profiles.avatar_url, populated by the Settings sheet
   *  「上傳頭像」 flow). Only the own-self preview consumes it. */
  globalAvatarUrl: string | null;
  familyId: string | null;
  error: string | null;
  onError: (msg: string | null) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  // Seed the form from the row. `relation` and the visual fields
  // are editable; `family_id` and `user_id` are off-limits (the DB
  // enforces the latter via RLS / triggers).
  const [draft, setDraft] = useState({
    display_name:    member?.display_name    ?? "",
    relation:        (member?.relation       ?? "other") as Rel,
    relation_label:  member?.relation_label  ?? "",
    initials:        member?.initials        ?? "",
    avatar_color:    member?.avatar_color    ?? AVATAR_PALETTE[0]!.value,
  });
  const [saving, setSaving] = useState(false);

  // `canEdit` is the user-facing check (creator OR editing
  // their own row). Without this, the form silently no-ops
  // for a member who tries to edit a sibling's row — the
  // RLS deny returns an error from .update() but the user has
  // no idea why their Save tap did nothing.
  const canEdit = isCreator || isSelf;

  // Keep the form in sync with the underlying row when the
  // user navigates between members. The component receives
  // a new `member` prop every open; the key prop on the
  // <AnimatePresence> wrapper in the parent would also
  // achieve this, but resetting state here keeps the
  // component decoupled from the parent's keying strategy.
  useEffect(() => {
    setDraft({
      display_name:   member?.display_name   ?? "",
      relation:       (member?.relation      ?? "other") as Rel,
      relation_label: member?.relation_label ?? "",
      initials:       member?.initials       ?? "",
      avatar_color:   member?.avatar_color   ?? AVATAR_PALETTE[0]!.value,
    });
    onError(null);
    // We intentionally don't depend on `onError` (it can be
    // an inline arrow that re-renders this effect).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.id]);

  const save = async () => {
    if (!canEdit) return;
    const name = draft.display_name.trim();
    if (name.length < 1 || name.length > 80) {
      onError("名稱要 1 - 80 字。");
      return;
    }
    const init = draft.initials.trim();
    if (init.length < 1 || init.length > 2) {
      onError("縮寫要 1 - 2 個字。");
      return;
    }
    if (initialsHasInvalid(init)) {
      onError("縮寫只可以用中英文字母、數字或 ·。");
      return;
    }
    setSaving(true);
    onError(null);
    try {
      if (!member) {
        // The sheet is open but no member is selected — nothing to
        // update. `canEdit` already guarded the path; this is
        // belt + braces against a future caller wiring the sheet
        // up with `member = null` and `canEdit = true`.
        onError("請先揀一位成員。");
        return;
      }
      // Whitelist the fields the user can change. Sending
      // `family_id` or `user_id` here would either be
      // rejected by `prevent_family_change` or the
      // user_id-policy on UPDATE; the safer move is to not
      // even attempt it.
      const { error: updErr } = await supabase
        .from("family_members")
        .update({
          display_name:   name,
          relation:       draft.relation,
          relation_label: draft.relation_label.trim().slice(0, 40) || null as unknown as string,
          initials:       init,
          avatar_color:   draft.avatar_color,
        } as Database["public"]["Tables"]["family_members"]["Update"])
        .eq("id", member.id);
      if (updErr) {
        onError(rlsMessage(updErr));
        return;
      }
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!member) return;
    if (!canEdit) return;
    if (!isCreator && !isSelf) return; // belt + braces; RLS agrees
    if (
      !window.confirm(
        `確定刪除「${member.display_name}」？此動作無法復原。`,
      )
    ) {
      return;
    }
    setSaving(true);
 onError(null);
    try {
      const { error: delErr } = await supabase
        .from("family_members")
        .delete()
        .eq("id", member.id);
      if (delErr) {
        onError(rlsMessage(delErr));
        return;
      }
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  // No-op in tests / logged-out state: skip the dialog.
  const open = Boolean(member);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="member-edit-sheet"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          role="dialog"
          aria-modal="true"
          aria-label="編輯家庭成員"
          className="fixed inset-0 z-40 flex items-end justify-center bg-foreground/40 sm:items-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 32 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl rounded-t-3xl border border-border/60 bg-card p-4 shadow-2xl sm:rounded-3xl sm:p-6"
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-muted sm:hidden" />
            <div className="mb-4 flex items-center gap-3">
              {/*
                Edit-sheet preview. When the row being edited IS the
                signed-in user's own member card AND they have uploaded
                a global avatar, show that photo on top of the
                gradient+initials preview so the visual identity lines
                up with the tree grid. The draft fields (initials /
                avatar_color) belong to family_members, not profiles,
                so editing the global avatar happens via the Settings
                sheet's「上傳頭像」 button, not here.
              */}
              <MemberAvatar
                initials={draft.initials.trim() || "·"}
                color={draft.avatar_color}
                avatarUrl={isSelf ? globalAvatarUrl : null}
                size="lg"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-base font-semibold">編輯成員</div>
                <div className="text-[11px] text-muted-foreground">
                  {canEdit
                    ? "更新名稱、關係或頭像"
                    : "你只可以編輯自己嘅成員資料"}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-1.5 text-muted-foreground hover:bg-secondary"
                aria-label="關閉"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            {!canEdit ? (
              <ViewOnlyPanel member={member} />
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void save();
                }}
                className="space-y-4"
              >
                <Field
                  label="名稱"
                  hint="顯示喺家族樹同 FamChat 嘅名稱。"
                >
                  <input
                    value={draft.display_name}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      setDraft((d) => ({ ...d, display_name: value }));
                    }}
                    maxLength={80}
                    className="h-10 w-full rounded-xl border border-border/60 bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                  />
                </Field>

                <Field
                  label="關係"
                  hint="揀一個會決定佢喺家族樹邊一行。"
                >
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {(Object.keys(RELATION_LABEL) as Rel[]).map((r) => {
                      const active = draft.relation === r;
                      return (
                        <button
                          key={r}
                          type="button"
                          onClick={() =>
                            setDraft((d) => ({ ...d, relation: r }))
                          }
                          className={cn(
                            "rounded-xl border px-2 py-2 text-left text-[12px] transition-colors",
                            active
                              ? "border-primary bg-primary/10 text-foreground"
                              : "border-border/60 bg-card hover:bg-secondary",
                          )}
                          aria-pressed={active}
                        >
                          <div className="font-medium">
                            {RELATION_LABEL[r]}
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            {RELATION_HINT[r]}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </Field>

                <Field
                  label="關係別名"
                  hint="可選，會顯示喺頭像下面。例：媽媽、嫲嫲、伯父。最多 40 字。"
                >
                  <input
                    value={draft.relation_label}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      setDraft((d) => ({ ...d, relation_label: value }));
                    }}
                    maxLength={40}
                    placeholder="例：媽媽"
                    className="h-10 w-full rounded-xl border border-border/60 bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                  />
                </Field>

                <div className="grid gap-3 sm:grid-cols-2">
                  <Field
                    label="縮寫"
                    hint="頭像入面顯示嘅 1-2 個字。"
                  >
                    <input
                      value={draft.initials}
                      onChange={(e) => {
                        // Read the value synchronously: the
                        // native event's currentTarget is reset
                        // to null by the time the lazy
                        // setDraft((d) => …) callback runs.
                        const value = e.currentTarget.value;
                        setDraft((d) => ({ ...d, initials: value }));
                      }}
                      maxLength={2}
                      className="h-10 w-full rounded-xl border border-border/60 bg-background px-3 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                    />
                  </Field>
                </div>

                <Field label="頭像顏色" hint="預設 8 色，揀一個最啱嘅。">
                  <div className="flex flex-wrap gap-2">
                    {AVATAR_PALETTE.map((c) => {
                      const active = draft.avatar_color === c.value;
                      return (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() =>
                            setDraft((d) => ({ ...d, avatar_color: c.value }))
                          }
                          className={cn(
                            "grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br text-[10px] font-semibold text-white",
                            c.preview,
                            active && "ring-2 ring-primary ring-offset-2 ring-offset-card",
                          )}
                          aria-label={`頭像顏色 ${c.label}`}
                          aria-pressed={active}
                        >
                          {draft.initials.trim() || "·"}
                        </button>
                      );
                    })}
                  </div>
                </Field>

                {error && (
                  <div
                    role="alert"
                    className="flex items-start gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] text-rose-700"
                  >
                    <span className="flex-1 leading-snug">{error}</span>
                    <button
                      type="button"
                      onClick={() => onError(null)}
                      aria-label="關閉錯誤"
                      className="rounded p-0.5 text-rose-500 hover:bg-rose-100"
                    >
                      ×
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {member && (isCreator || isSelf) && (
                    <button
                      type="button"
                      onClick={() => void remove()}
                      disabled={saving}
                      className="inline-flex h-10 items-center gap-1 rounded-full border border-rose-200 bg-card px-3 text-xs font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      刪除成員
                    </button>
                  )}
                  <div className="ml-auto flex gap-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="h-10 rounded-full border border-border/60 bg-card px-4 text-sm"
                    >
                      取消
                    </button>
                    <motion.button
                      type="submit"
                      whileTap={{ scale: 0.96 }}
                      transition={{ type: "spring", stiffness: 400, damping: 22 }}
                      disabled={saving}
                      className="inline-flex h-10 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm shadow-primary/30 disabled:opacity-50"
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden />
                      {saving ? "儲存中…" : "儲存"}
                    </motion.button>
                  </div>
                </div>
              </form>
            )}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

/* Read-only fallback shown when the user lacks RLS to edit
 * the selected member. Renders the row as plain text so the
 * dialog still serves as a "view" surface. */
function ViewOnlyPanel({ member }: { member: Database["public"]["Tables"]["family_members"]["Row"] | null }) {
  if (!member) return null;
  return (
    <div className="space-y-2 text-sm">
      <Row label="名稱" value={member.display_name} />
      <Row label="關係" value={RELATION_LABEL[relFromString(member.relation)]} />
      {member.relation_label && (
        <Row label="別名" value={member.relation_label} />
      )}
      <Row label="縮寫" value={member.initials} />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-12 text-[10px] text-muted-foreground">{label}</span>
      <span className="text-foreground/85">{value}</span>
    </div>
  );
}

function Field({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-[11px] font-medium text-muted-foreground">
        {label}
      </span>
      <div className="mt-1">{children}</div>
      {hint && (
        <div className="mt-1 text-[10px] text-muted-foreground">{hint}</div>
      )}
    </label>
  );
}

/* Sanity check for the initials input. The DB check is
 * `char_length between 1 and 2` and any character is allowed
 * in the column type (text). For the UI we restrict to
 * letters / digits / the middle-dot default so the avatar
 * renders a sensible character on every locale. */
function initialsHasInvalid(s: string): boolean {
  // 1-2 chars from the allow-list (letter, digit, or middle-dot).
  return !/^[\p{L}\p{N}·\u00b7]{1,2}$/u.test(s);
}

/* Translate a Supabase / RLS error into something the user
 * can act on. The default error.message is "new row violates
 * row-level security policy for table family_members" which
 * is technically correct but doesn't tell the user "ask the
 * family admin to add you". */
function rlsMessage(err: { message?: string; code?: string }): string {
  const m = (err.message ?? "").toLowerCase();
 if (m.includes("row-level security") || m.includes("rls") || err.code === "42501") {
    return "你冇權限呢個動作。請聯絡家庭建立者。";
  }
  if (m.includes("check constraint") || err.code === "23514") {
    return "輸入嘅資料唔啱格式。請檢查名稱、縮寫或顏色。";
  }
  if (m.includes("duplicate key") || err.code === "23505") {
    return "已經有同名成員，請改另一個名。";
  }
  if (m.includes("foreign key") || err.code === "23503") {
    return "關聯嘅資料已經唔存在，請重新整理再試。";
  }
  return err.message ?? "儲存失敗，請重試。";
}

/* ───────────────────  CrestEditor  ───────────────────
 *
 * Self-contained crest card body. Renders the preview circle
 * (uploaded image when set, selected-but-not-uploaded local file
 * when one is picked, the rose-amber-violet gradient + Crown
 * fallback otherwise), the name input, and the action cluster
 * for choose / replace / remove.
 *
 * Lives outside the main component so the upload lifecycle
 * (state machine, object URL, file input) doesn't pollute the
 * FamTreeView hook list — a future "edit crest" sheet can copy
 * it intact without dragging the rest of the tree along.
 */
function CrestEditor({
  familyId,
  crestName,
  imageUrl,
  pendingPreviewUrl,
  hasPendingFile,
  uploadState,
  fileInputRef,
  onPickFile,
  onChooseFile,
  onCancelPending,
  onConfirmPending,
  onRemove,
}: {
  familyId: string | null;
  crestName: string;
  imageUrl: string | null;
  pendingPreviewUrl: string | null;
  hasPendingFile: boolean;
  uploadState: CrestUploadState;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onPickFile: () => void;
  onChooseFile: (file: File) => void;
  onCancelPending: () => void;
  onConfirmPending: () => void;
  onRemove: () => void;
}) {
  const busy =
    uploadState.phase === "compressing"
    || uploadState.phase === "uploading"
    || uploadState.phase === "saving";

  const phaseLabel = ((): string => {
    switch (uploadState.phase) {
      case "compressing": return "調整大小中…";
      case "uploading":   return "上傳中…";
      case "saving":      return "儲存中…";
      default:            return "";
    }
  })();

  // The preview circle shows the local file first (so the user
  // sees what they're about to upload), then the persisted
  // server-side URL, then the gradient-with-Crown fallback. The
  // `key` ties the browser's decoded-image cache to the URL —
  // when the URL changes after a successful upload, the <img>
  // re-fetches from scratch instead of showing a stale frame.
  const previewSrc = pendingPreviewUrl ?? imageUrl;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 rounded-2xl bg-secondary/40 p-3">
        <span
          aria-hidden
          className={cn(
            "grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-full text-2xl",
            // Show the warm rose-amber-violet only when nothing is
            // uploaded — once the family has a real crest we let
            // the image fill the circle so the visual identity is
            // coherent across surfaces.
            !previewSrc && "bg-gradient-to-br from-rose-300 via-amber-200 to-violet-300",
            busy && "opacity-70",
          )}
        >
          {previewSrc ? (
            <img
              src={previewSrc}
              alt="家徽預覽"
              className="h-full w-full object-cover"
              key={previewSrc}
            />
          ) : (
            <Crown className="h-5 w-5 text-rose-700" aria-hidden />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-muted-foreground">家庭名稱</div>
          <div className="mt-0.5 truncate text-sm font-medium">{crestName || "未命名家庭"}</div>
        </div>
      </div>

      {phaseLabel && (
        <p role="status" className="text-[11px] text-muted-foreground">
          {phaseLabel}
        </p>
      )}

      {!familyId ? (
        <p role="alert" className="text-[11px] text-rose-700">
          未載入家庭資料，無法上傳家徽。
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={onPickFile}
          >
            {imageUrl ? (
              <>
                <Camera className="h-4 w-4" aria-hidden />
                更換家徽
              </>
            ) : (
              <>
                <ImageIcon className="h-4 w-4" aria-hidden />
                上傳家徽
              </>
            )}
          </Button>
          {imageUrl && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={onRemove}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
              移除家徽
            </Button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/*"
            className="sr-only"
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              if (f) onChooseFile(f);
            }}
          />
        </div>
      )}

      {hasPendingFile && !busy && (
        <div className="rounded-2xl border border-border/60 bg-card p-3">
          <p className="text-sm">
            已選擇新家徽，確定要儲存？檔案喺上傳前只係喺你嘅裝置上。
          </p>
          <div className="mt-3 flex gap-2">
            <Button type="button" size="sm" onClick={onConfirmPending}>
              儲存家徽
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={onCancelPending}>
              取消
            </Button>
          </div>
        </div>
      )}

      {uploadState.phase === "error" && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-[11px] text-rose-700"
        >
          <span className="flex-1 leading-snug">{uploadState.message}</span>
          <button
            type="button"
            onClick={() => onCancelPending()}
            aria-label="關閉錯誤"
            className="rounded p-0.5 text-rose-500 hover:bg-rose-100"
          >
            ×
          </button>
        </div>
      )}

      {uploadState.phase === "done" && (
        <div
          role="status"
          className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-800"
        >
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
          家徽已更新。
        </div>
      )}
    </div>
  );
}

export default FamTreeView;

export type { Rel as FamTreeRel, Node as FamTreeNode };
